import { isPlatformBrowser } from '@angular/common';
import {
  DestroyRef,
  Directive,
  effect,
  ElementRef,
  type EmbeddedViewRef,
  inject,
  input,
  model,
  PLATFORM_ID,
  TemplateRef,
  untracked,
  ViewContainerRef,
} from '@angular/core';
import { type Placement } from '@floating-ui/dom';
import { PopoverInstance, type PopoverInstanceConfig } from './popover-instance';
import { POPOVER_VARIATIONS, type PopoverTriggerType, type PopoverVariationName } from './popover-variations';

type PopoverContent = string | TemplateRef<unknown> | null | undefined;

/**
 * Popover/tooltip directive backed by @floating-ui/dom via {@link PopoverInstance}.
 * Supports string and TemplateRef content, variations, placement, triggers,
 * host-width matching and imperative show/hide.
 *
 * Every input defaults to its variation's preset; only set an input to override it.
 */
@Directive({
  selector: '[rcpPopover]',
  exportAs: 'rcpPopover',
})
export class RcpPopoverDirective {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly host = inject(ElementRef).nativeElement as HTMLElement;
  private readonly viewContainerRef = inject(ViewContainerRef);

  readonly rcpPopover = input<PopoverContent>();
  readonly rcpPopoverVariation = input<PopoverVariationName>('default');
  readonly rcpPopoverPlacement = input<Placement>();
  readonly rcpPopoverTrigger = input<PopoverTriggerType>();
  /** Any CSS length, e.g. `400px` or `unset`. */
  readonly rcpPopoverMaxWidth = input<string>();
  /** Context for a TemplateRef content, exposed as both `$implicit` and `data`. */
  readonly rcpPopoverData = input<unknown>();
  readonly rcpPopoverInteractive = input<boolean>();
  readonly rcpPopoverUseHostWidth = input(false);
  readonly rcpPopoverIsEnabled = input(true);
  readonly rcpPopoverIsVisible = model(false);

  private instance?: PopoverInstance;
  private embeddedView?: EmbeddedViewRef<unknown>;

  constructor() {
    inject(DestroyRef).onDestroy(() => this.teardown());

    // Rebuild the popover when its content or any structural input changes.
    effect(() => {
      this.rcpPopover();
      this.rcpPopoverData();
      const config = this.buildConfig();
      untracked(() => this.rebuild(config));
    });

    // Toggle enabled state without rebuilding.
    effect(() => {
      const isEnabled = this.rcpPopoverIsEnabled();
      untracked(() => {
        if (isEnabled) {
          this.instance?.enable();
        } else {
          this.instance?.disable();
        }
      });
    });
  }

  show(): void {
    this.instance?.show();
  }

  hide(): void {
    this.instance?.hide();
  }

  enable(): void {
    this.instance?.enable();
  }

  disable(): void {
    this.instance?.disable();
  }

  private buildConfig(): PopoverInstanceConfig {
    const variationName = this.rcpPopoverVariation();
    const variation = POPOVER_VARIATIONS[variationName];
    return {
      ...variation,
      variationName,
      placement: this.rcpPopoverPlacement() ?? variation.placement,
      trigger: this.rcpPopoverTrigger() ?? variation.trigger,
      interactive: this.rcpPopoverInteractive() ?? variation.interactive,
      maxWidth: this.rcpPopoverMaxWidth(),
      useHostWidth: this.rcpPopoverUseHostWidth(),
      onVisibleChange: (isVisible) => this.rcpPopoverIsVisible.set(isVisible),
    };
  }

  private rebuild(config: PopoverInstanceConfig): void {
    // dont show popovers during ssg
    if (isPlatformBrowser(this.platformId)) {
      const wasVisible = this.instance?.visible ?? false;
      this.teardown();

      this.instance = new PopoverInstance(this.host, config, () => this.resolveContent());
      if (!this.rcpPopoverIsEnabled()) {
        this.instance.disable();
      } else if (wasVisible) {
        this.instance.show();
      }
    }
  }

  private resolveContent(): string | Node[] {
    const content = this.rcpPopover();
    if (content instanceof TemplateRef) {
      this.embeddedView?.destroy();
      this.embeddedView = this.viewContainerRef.createEmbeddedView(content, {
        $implicit: this.rcpPopoverData(),
        data: this.rcpPopoverData(),
      });
      this.embeddedView.detectChanges();
      return this.embeddedView.rootNodes as Node[];
    }
    return content ?? '';
  }

  private teardown(): void {
    this.instance?.destroy();
    this.instance = undefined;
    this.embeddedView?.destroy();
    this.embeddedView = undefined;
  }
}
